"use client";

/**
 * Global "you've hit a free cap" upsell trigger.
 *
 * Any capped save path calls openPinLimit(limit, kind) when the backend
 * rejects with reason "limit_reached"; a single <PinLimitSheet /> mounted at
 * the app root subscribes and shows the matching Pro upsell. Module-level (no
 * provider) so it works from cards in grids, drawers, and map popups across
 * unrelated trees.
 *
 * `kind` picks the copy + manage link:
 *   "pins"       - My Map saved-listing cap (card bookmark, My-Map editor)
 *   "properties" - My Properties portfolio cap
 *   "account"    - a device-bound heart or saved search hit the
 *                  anonymous ladder (or push was refused): the ask is a
 *                  sign-in, not Pro
 *   "follow"     - a residence follow could not use push (blocked or
 *                  unsupported browser) and the visitor is signed out:
 *                  sign in and the follow completes by Telegram or e-mail
 */
import { useEffect, useState } from "react";

export type PinLimitKind = "pins" | "properties" | "account" | "follow";

interface LimitState {
  limit: number;
  kind: PinLimitKind;
}

type Listener = (s: LimitState | null) => void;

const listeners = new Set<Listener>();
let current: LimitState | null = null;

export function openPinLimit(limit: number, kind: PinLimitKind = "pins"): void {
  current = { limit, kind };
  listeners.forEach((l) => l(current));
}

export function closePinLimit(): void {
  current = null;
  listeners.forEach((l) => l(current));
}

export function usePinLimit(): {
  limit: number | null;
  kind: PinLimitKind;
  close: () => void;
} {
  const [state, setState] = useState<LimitState | null>(current);
  useEffect(() => {
    listeners.add(setState);
    setState(current);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return {
    limit: state?.limit ?? null,
    kind: state?.kind ?? "pins",
    close: closePinLimit,
  };
}
